import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';

import { ErrorHttpResponse, HttpConfig, SuccessHttpResponse } from '../models/https';
import useHttps from './useHttps';

// Small building blocks for the one-hook-per-endpoint files under
// hooks/dashboard/*: a GET that keeps its data, and a write that toasts.

export const errorMessage = (err: AxiosError<ErrorHttpResponse>, fallback: string): string => {
  const message = err?.response?.data?.message ?? (err?.response?.data as any)?.detail;
  return typeof message === 'string' ? message : fallback;
};

export const useGet = <T,>(fallbackError = 'Failed to load') => {
  const request = useHttps();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(
    (url: string, params?: Record<string, any>) => {
      request(
        { url, method: 'get', params },
        ({ data: body }: SuccessHttpResponse<T>) => setData(body),
        setLoading,
        (err: AxiosError<ErrorHttpResponse>) => console.error(errorMessage(err, fallbackError))
      );
    },
    [request, fallbackError]
  );

  return { data, loading, fetch, setData };
};

export const useSend = <T = any,>(fallbackError = 'Something went wrong') => {
  const request = useHttps();
  const [loading, setLoading] = useState(false);

  const send = useCallback(
    (config: HttpConfig, onDone?: (data: T, message: string) => void, onFail?: (message: string) => void) => {
      request(
        config,
        ({ data, message }: SuccessHttpResponse<T>) => onDone?.(data, message),
        setLoading,
        (err: AxiosError<ErrorHttpResponse>) => {
          const message = errorMessage(err, fallbackError);
          if (onFail) onFail(message);
          else toast.error(message);
        }
      );
    },
    [request, fallbackError]
  );

  return { loading, send };
};
